import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, FileCheck2, ClipboardList } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { SuspensionControl } from "@/components/SuspensionControl";

const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/customers/${id}`);
      setD(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, [id]);

  if (!d) return <div className="container-x py-20 text-[#5C6B64]">Loading…</div>;

  const fa = (k) => (e) => setD({ ...d, account: { ...d.account, [k]: e.target.value } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${d.account.id}`, {
        name: d.account.name || undefined,
        email: d.account.email || undefined,
        phone: d.account.phone || undefined,
      });
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-x py-12" data-testid="admin-customer-detail">
      <AdminTabs />
      <button onClick={() => navigate("/admin/customers")} className="text-sm text-[#5C6B64] hover:text-[#1E3F33] inline-flex items-center gap-1.5 mb-4" data-testid="admin-customer-back">
        <ArrowLeft className="w-4 h-4" /> Back to customers
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold">{d.account.name || d.account.username}</h1>
        <button onClick={save} disabled={saving} data-testid="admin-customer-save" className="btn-primary inline-flex items-center gap-2">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="grid lg:grid-cols-12 gap-6 mt-8">
        <div className="lg:col-span-7 space-y-6">
          <div className="card-soft">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-4">Account</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="label-soft">Name</label><input data-testid="admin-cust-name" className="input-soft" value={d.account.name || ""} onChange={fa("name")} /></div>
              <div><label className="label-soft">Username</label><input className="input-soft bg-[#F0EFEA]" value={d.account.username || ""} disabled /></div>
              <div><label className="label-soft">Email</label><input data-testid="admin-cust-email" type="email" className="input-soft" value={d.account.email || ""} onChange={fa("email")} /></div>
              <div><label className="label-soft">Phone</label><input data-testid="admin-cust-phone" className="input-soft" value={d.account.phone || ""} onChange={fa("phone")} /></div>
            </div>
            <p className="text-xs text-[#8A9A86] mt-3">Notifications consent: {d.notify_consent ? "Yes" : "No"} · Joined {fmt(d.account.created_at)}</p>
          </div>

          <div className="card-soft" data-testid="admin-cust-requests">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-[#CC5A3A]" /> Service requests ({d.service_requests.length})</h2>
            {d.service_requests.length === 0 ? (
              <p className="text-sm text-[#8A9A86]">No service requests.</p>
            ) : (
              <ul className="space-y-3">
                {d.service_requests.map((s) => (
                  <li key={s.id} className="border border-[#1E3F33]/10 rounded-xl p-3" data-testid={`admin-cust-request-${s.id}`}>
                    <div className="text-sm text-[#1E3F33]">{(s.services || []).join(", ") || "—"}</div>
                    <div className="text-xs text-[#5C6B64] mt-1">Zip: {(s.zip_codes || []).join(", ")} · {s.when_label}</div>
                    <div className="text-[11px] text-[#8A9A86] mt-0.5">{fmt(s.created_at)}{s.acknowledgment ? ` · acknowledgment v${s.acknowledgment.version}` : ""}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="lg:col-span-5 space-y-6">
          <SuspensionControl account={d.account} onChange={load} />
          <div className="card-soft" data-testid="admin-cust-agreements">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-3 flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-[#2D6A4F]" /> Agreements</h2>
            {(d.agreements || []).length === 0 ? (
              <p className="text-sm text-[#8A9A86]">No agreement records on file.</p>
            ) : (
              <ul className="space-y-2">
                {(d.agreements || []).map((a, i) => (
                  <li key={i} className="text-sm border border-[#1E3F33]/10 rounded-lg p-2.5">
                    <div className="text-[#1E3F33] font-medium">{a.title}</div>
                    <div className="text-[11px] text-[#8A9A86] mt-0.5">v{a.version} · {fmt(a.accepted_at)}{a.ip ? ` · IP ${a.ip}` : ""}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
